import { useState } from 'react';
import { Plus, Heart, UtensilsCrossed } from 'lucide-react';

import { useFavorites } from '@/store/favorites.store';
import { formatRub } from '@/utils/format';
import { asAbsoluteUrl } from '@/utils/url';
import type { MenuResponse } from '@/services/client';

type Dish = MenuResponse['categories'][number]['dishes'][number];

type Variant = 'grid' | 'row';

type Props = {
  dish: Dish;
  onOpen: () => void;
  onQuickAdd: () => void;
  variant?: Variant;
};

// Универсальная карточка блюда — сеткой (меню) и строкой (избранное/поиск).
// UI намеренно минималистичный: «+» кнопка-кружок вместо широкой «В корзину»,
// плейсхолдер — лёгкий серый с иконкой столовых приборов (без больших эмодзи).
export default function DishCard({
  dish,
  onOpen,
  onQuickAdd,
  variant = 'grid',
}: Props) {
  const fav = useFavorites((s) => s.ids.includes(dish.id));
  const toggleFav = useFavorites((s) => s.toggle);

  const hasMods = dish.modifiers.length > 0;

  const handleAdd = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasMods) onOpen();
    else onQuickAdd();
  };

  const handleFav = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFav(dish.id);
  };

  if (variant === 'row') {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen();
          }
        }}
        className="relative flex gap-3 rounded-2xl bg-white p-3 cursor-pointer active:scale-[.99] transition-transform border border-slate-100"
      >
        <DishImage dish={dish} className="w-20 h-20 rounded-xl shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-[15px] leading-snug line-clamp-2 pr-8">
            {dish.name}
          </div>
          {dish.weight && (
            <div className="text-xs text-slate-400 mt-0.5">{dish.weight}</div>
          )}
          <div className="flex items-center justify-between mt-2">
            <div className="font-semibold text-[15px] tabular-nums">
              {formatRub(dish.price)}
            </div>
            <AddButton onClick={handleAdd} hasMods={hasMods} size="sm" />
          </div>
        </div>
        <FavButton active={fav} onClick={handleFav} />
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className="group flex flex-col text-left cursor-pointer active:scale-[.98] transition-transform"
    >
      <div className="relative">
        <DishImage dish={dish} className="aspect-square w-full rounded-2xl" />
        <FavButton active={fav} onClick={handleFav} />
        {dish.photos && dish.photos.length > 1 && (
          <span className="absolute top-2 left-2 text-[10px] bg-black/55 text-white rounded-md px-1.5 py-0.5">
            +{dish.photos.length - 1}
          </span>
        )}
      </div>

      <div className="pt-2.5 pb-1 px-0.5 flex flex-col">
        <div className="font-medium text-[14px] leading-snug line-clamp-2 min-h-[2.4em]">
          {dish.name}
        </div>
        {dish.weight && (
          <div className="text-[11px] text-slate-400 mt-0.5">{dish.weight}</div>
        )}

        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="font-semibold text-[15px] tabular-nums">
            {formatRub(dish.price)}
          </div>
          <AddButton onClick={handleAdd} hasMods={hasMods} />
        </div>
      </div>
    </div>
  );
}

// ─── Внутренние вспомогательные компоненты ────────────────────────

function DishImage({ dish, className = '' }: { dish: Dish; className?: string }) {
  const cover = asAbsoluteUrl(dish.photos?.[0] ?? dish.photoUrl ?? null);
  const [broken, setBroken] = useState(false);

  if (!cover || broken) {
    return (
      <div
        className={`${className} flex items-center justify-center bg-slate-50 text-slate-300`}
      >
        <UtensilsCrossed size={22} strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div className={`${className} overflow-hidden bg-slate-50`}>
      <img
        src={cover}
        alt={dish.name}
        loading="lazy"
        decoding="async"
        className="w-full h-full object-cover"
        onError={() => setBroken(true)}
      />
    </div>
  );
}

function AddButton({
  onClick,
  hasMods,
  size = 'md',
}: {
  onClick: (e: React.MouseEvent) => void;
  hasMods: boolean;
  size?: 'sm' | 'md';
}) {
  const dim = size === 'sm' ? 'w-8 h-8' : 'w-9 h-9';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={hasMods ? 'Выбрать модификаторы' : 'Добавить в корзину'}
      className={`${dim} shrink-0 rounded-full bg-brand-600 text-white flex items-center justify-center shadow-sm hover:bg-brand-700 transition-colors`}
    >
      <Plus size={18} strokeWidth={2.5} />
    </button>
  );
}

function FavButton({
  active,
  onClick,
}: {
  active: boolean;
  onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={active ? 'Убрать из избранного' : 'В избранное'}
      className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center backdrop-blur-sm transition-colors ${
        active
          ? 'bg-white/95 text-rose-500'
          : 'bg-white/80 text-slate-400 hover:text-rose-500'
      }`}
    >
      <Heart size={15} fill={active ? 'currentColor' : 'none'} />
    </button>
  );
}
